import { CircularProgress, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import type { FC } from "react";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { XCircle } from "../../../../../styles/icons/x-circle";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import { ConfirmDialog } from "../../../../generic/components/confirm-dialog";
import type { CustomHeader } from "../../server/supabase-custom-email-header-service";
import { SupabaseCustomEmailHeaderService } from "../../server/supabase-custom-email-header-service";

export const DeleteHeaderButton: FC<{
  customHeader: CustomHeader;
  onDelete: () => void;
}> = ({ customHeader, onDelete }) => {
  const [removing, setRemoving] = useState(false);
  const confirmDialog = useDialog();

  const handleDelete = useSupabaseCallback(
    async ({ supabase }, header: CustomHeader): Promise<void> => {
      setRemoving(true);

      const error = await new SupabaseCustomEmailHeaderService(supabase).delete(header.id);

      if (!error) {
        toast.success(`Header "${header.display_name}" deleted successfully.`);
        onDelete();
      } else {
        toast.error(`Something went wrong when deleting "${header.display_name}"`);
        setRemoving(false);
      }
    },
    [onDelete],
  );

  const handleConfirmDelete = useCallback((): void => {
    confirmDialog.handleClose();
    void handleDelete(customHeader).catch(console.error);
  }, [confirmDialog, customHeader, handleDelete]);

  return (
    <>
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={`Delete "${customHeader.display_name}"`} placement="right">
            {removing ? <CircularProgress size="small" /> : <XCircle fontSize="small" />}
          </Tooltip>
        }
        label={`Delete "${customHeader.display_name}"`}
        aria-label={`Delete "${customHeader.display_name}"`}
        showInMenu={false}
        onClick={() => confirmDialog.handleOpen()}
      />
      <ConfirmDialog
        open={confirmDialog.open}
        title={`Delete "${customHeader.display_name}"`}
        description={`This means it can no longer be used to add custom headers to email Communications. You can always recreate it again later if needed.`}
        onConfirm={handleConfirmDelete}
        onCancel={confirmDialog.handleClose}
        confirmText="Delete custom header"
      />
    </>
  );
};
